import { css, useTheme } from "@emotion/react";
import { ethers } from "ethers";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useRecoilValue } from "recoil";
import { useAccount } from "wagmi";

import { ampBalanceAtom } from "../../atoms";
import {
  Button,
  Buttons,
  Flex,
  Label,
  SecondaryButton,
  Spacer,
} from "../../base";
import { isRealError } from "../../lib/errors";
import { formatTokens, toDecimal } from "../../lib/format";
import { appSelector } from "../../modules/apps";
import { useConfig } from "../../modules/config";
import {
  useAmpContract,
  useEtherCall,
  useFormTokenValue,
} from "../../modules/hooks";
import AppSelector from "../AppSelector";
import ErrorMessage from "../ErrorMessage";
import Slider, { SliderInput } from "../Slider";

const FLAG_CHANGE_PARTITION =
  "0xffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffffff";

const PRECISION = 3;

const StepSupply = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { color } = useTheme();
  const { address: account } = useAccount();
  const config = useConfig();

  const ampBalance = useRecoilValue(ampBalanceAtom);
  const [value, amount, onEdit] = useFormTokenValue();

  const [error, setError] = useState();

  const availableTokens = toDecimal(ampBalance);

  const [selectedAppId, setSelectedAppId] = useState();
  const app = useRecoilValue(appSelector(selectedAppId));

  const canSupply = ampBalance.gt(0) && selectedAppId;
  const canContinue = amount.gt(0) && ampBalance.gte(amount) && selectedAppId;

  const handleUpdateField = (val) => {
    onEdit(val);
  };

  const etherCall = useEtherCall(useAmpContract(), {
    confirmations: 8,
    watchKey: "supply",
  });

  const onClickContinue = async () => {
    if (!canContinue || !selectedAppId) {
      return;
    }

    const { error } = await etherCall((contract) => {
      const data = ethers.utils.defaultAbiCoder.encode(
        ["bytes32", "bytes32"],
        [FLAG_CHANGE_PARTITION, app.partition]
      );

      console.log(
        "contract transferByPartition\n",
        ethers.utils.formatBytes32String(""),
        account,
        config.contracts.flexaCollateralManager,
        amount,
        data,
        "0x"
      );
      return contract.transferByPartition(
        ethers.utils.formatBytes32String(""),
        account,
        config.contracts.flexaCollateralManager,
        amount,
        data,
        "0x"
      );
    });

    if (error) {
      setError(error);
      return;
    }

    history.push("/account");
  };

  const onDrag = useCallback(
    (factor: number) => {
      onEdit(
        factor > 0 ? (toDecimal(ampBalance).toNumber() * factor).toFixed(0) : ""
      );
    },
    [ampBalance, onEdit]
  );

  return (
    <div>
      <AppSelector
        actionText={t("common.supply")}
        selected={selectedAppId}
        onSelect={(appId) => setSelectedAppId(appId)}
      />

      <Spacer size={26} />

      <Flex between>
        <Slider
          draggable
          color={app ? app.color : color.highlight}
          disabled={!canSupply}
          maxPosition={1}
          position={
            availableTokens.isZero()
              ? 0
              : (
                  toDecimal(amount).toNumber() / availableTokens.toNumber()
                ).toPrecision(PRECISION)
          }
          onDrag={onDrag}
        />
        <Spacer col size={30} />

        <SliderInput
          css={[
            css`
              flex: 1;
              width: auto;
              min-width: 0;
            `,
            ampBalance.lt(amount || 0) &&
              css`
                color: red;
              `,
          ]}
          disabled={!canSupply}
          placeholder="0"
          value={value}
          onChange={(e) => {
            handleUpdateField(e.target.value);
          }}
        />
      </Flex>

      <Spacer size={10} />

      <Label
        light
        color={color.highlight}
        css={
          !canSupply || (ampBalance && amount && ampBalance.eq(amount))
            ? css`
                opacity: 0.25;
              `
            : null
        }
        onClick={() => {
          if (canSupply) {
            onEdit(formatTokens(ampBalance, false));
          }
        }}
      >
        {formatTokens(ampBalance, false)} AMP{" "}
        {t("common.available").toLowerCase()}
      </Label>

      <Spacer size={24} />

      {isRealError(error) && (
        <>
          <ErrorMessage
            error={error}
            message="An error occurred while attempting the transaction. Please try again."
          />
        </>
      )}

      <Spacer size={40} />
      <Buttons>
        <Button
          onClick={() => {
            history.goBack();
          }}
        >
          {t("common.cancel")}
        </Button>
        <SecondaryButton
          disabled={!canContinue || !selectedAppId}
          onClick={onClickContinue}
        >
          {t("common.continue")}
        </SecondaryButton>
      </Buttons>
    </div>
  );
};

export default StepSupply;
